import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { ComponentTask, UpdateAction } from '../shared/model';
import { Observable, tap } from 'rxjs';
import { Condition } from '@urbannex/ofbiz-forms';

interface UpdaterState {
  componentTask: ComponentTask | null;
  activeStepIndex: number;
  context: any;
  selectedRow: any[];
}

@Injectable({
  providedIn: 'root'
})
export class TaskStoreService extends ComponentStore<UpdaterState>{

  constructor() {
    super({ componentTask: null, activeStepIndex: 0, context: {}, selectedRow:[]});
  }

  // Set Initial ComponentTask State
  // Set ComponentTask
  readonly setComponentTask = this.updater((state, componentTask: ComponentTask) => ({
    ...state,
    componentTask,
    activeStepIndex: 0,
    selectedRow:[]
  }));

  //Selectors 
  readonly componentTask$ = this.select(state => state.componentTask);
  readonly activeStepIndex$ = this.select(state => state.activeStepIndex);
  readonly context$ = this.select(state => state.context);
  readonly selectedRow$ = this.select(state => state.selectedRow);
  readonly activeUpdateAction$ = this.select(state => {
    const { componentTask, activeStepIndex } = state;
    return componentTask?.updater.updateActions[activeStepIndex] ?? null;
  });
  //Move to next
  readonly goNext = this.updater((state) => {
    const { componentTask, activeStepIndex, context } = state;
    const updateActions = componentTask?.updater.updateActions ?? [];
  
    let nextStepIndex = activeStepIndex + 1;
  
    // Find the next valid step by checking conditions
    while (nextStepIndex < updateActions.length) {
      const nextAction = updateActions[nextStepIndex];
  
      if (!nextAction.condition || this.evaluateCondition(nextAction.condition, context)) {
        return { ...state, activeStepIndex: nextStepIndex };
      }
  
      nextStepIndex++;
    }
  
    return state; // Stay on the last valid step if no further forms match
  });
  

  // Move to the previous step
  readonly goPrevious = this.updater((state) => ({
    ...state,
    activeStepIndex: Math.max(0, state.activeStepIndex - 1),
  }));

  // Update context when a form is submitted
  readonly updateSelectedRow = this.updater((state, selectedRow:any[]) => {
    return {
      ...state,
      selectedRow
    }
  })
  readonly updateContext = this.updater((state, newContext:any) => ({
    ...state,
    context: { ...state.context, ...newContext },
  }));



  readonly evaluateConditions = this.effect<{ context: any }>((trigger$) => {
    return trigger$.pipe(
      tap(({ context }) => {
        const { componentTask } = this.get();
        if (!componentTask) return;
  
        const { updateActions } = componentTask.updater;
        
        // Find the first updateAction whose condition is met
        const nextIndex = updateActions.findIndex((action) =>
          action.condition ? this.evaluateCondition(action.condition, context) : false
        );
  
        if (nextIndex !== -1) {
          console.log(nextIndex)
          this.patchState({ activeStepIndex: nextIndex, context });
        }
      })
    );
  });

  evaluateCondition(condition: Condition, context: any): boolean {
    if (!condition) return false;
    
    // Evaluate a single condition
    const evaluateSingle = (cond: Condition) => {
      const fieldValue = context[cond.field];
  
      switch (cond.operator) {
        case 'equals': return fieldValue === cond.value;
        case 'not-equal': return fieldValue !== cond.value;
        case 'less-than': return fieldValue < cond.value;
        case 'greater-than': return fieldValue > cond.value;
        case 'less-than-or-equals': return fieldValue <= cond.value;
        case 'greater-than-or-equals': return fieldValue >= cond.value;
        case 'contains': return Array.isArray(fieldValue) && fieldValue.includes(cond.value);
        default: return false;
      }
    };
  
    // Handle nested conditions
    if (condition.nestedConditions) {
      const results = condition.nestedConditions.map(cond => evaluateSingle(cond));
  
      return condition.combine === 'AND'
        ? results.every(result => result) // All conditions must be true
        : results.some(result => result); // At least one condition must be true
    }
  
    return evaluateSingle(condition);
  }

  // Update Context after form submission
  /* readonly updateContext = this.updater((state, newContext: any) => {
    this.evaluateConditions({ context: newContext }); // Evaluate conditions after update
    return {
      ...state,
      context: { ...state.context, ...newContext },
    };
  }); */
  
  
}
